import { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

function MusicPlayer() {
  // Load saved preferences from localStorage
  const getSavedPreference = () => {
    const saved = localStorage.getItem('dowurk-ambient-muted');
    return saved === 'true';
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(getSavedPreference);
  const [volume, setVolume] = useState(0.15); // Lower default volume for ambient
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInitialPrompt, setShowInitialPrompt] = useState(false);
  const audioRef = useRef(null);
  const hasInteracted = useRef(false);
  const initialPromptShown = useRef(false);

  // Check if this is user's first visit (no preference saved)
  useEffect(() => {
    const hasVisited = localStorage.getItem('dowurk-ambient-visited');
    if (!hasVisited && !initialPromptShown.current) {
      initialPromptShown.current = true;
      // Show prompt after a short delay
      setTimeout(() => {
        setShowInitialPrompt(true);
      }, 2000);
    }
  }, []);

  // Attempt auto-play when component mounts (if not muted)
  useEffect(() => {
    if (!isMuted && audioRef.current && !hasInteracted.current) {
      audioRef.current.volume = volume;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          hasInteracted.current = true;
        })
        .catch(err => {
          console.log('Auto-play blocked - waiting for user interaction');
          // Browser blocked auto-play, we'll play on first user interaction
        });
    }
  }, [isMuted, volume]);

  // Handle first user interaction to start audio (for browsers that block auto-play)
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!isMuted && audioRef.current && !isPlaying && !hasInteracted.current) {
        audioRef.current.volume = volume;
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            hasInteracted.current = true;
          })
          .catch(() => {});
      }
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [isMuted, isPlaying, volume]);

  // Update audio when muted/volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      if (isMuted) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else if (!isPlaying && hasInteracted.current) {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(() => {});
      }
    }
  }, [isMuted, volume, isPlaying]);

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    localStorage.setItem('dowurk-ambient-muted', newMuted.toString());
    localStorage.setItem('dowurk-ambient-visited', 'true');
    
    if (!newMuted && audioRef.current) {
      hasInteracted.current = true;
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(() => {});
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume[0]);
    if (audioRef.current) {
      audioRef.current.volume = newVolume[0];
    }
  };

  const handlePromptResponse = (allowSound) => {
    setShowInitialPrompt(false);
    localStorage.setItem('dowurk-ambient-visited', 'true');
    
    if (allowSound) {
      setIsMuted(false);
      localStorage.setItem('dowurk-ambient-muted', 'false');
      if (audioRef.current) {
        hasInteracted.current = true;
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(() => {});
      }
    } else {
      setIsMuted(true);
      localStorage.setItem('dowurk-ambient-muted', 'true');
    }
  };

  return (
    <>
      {/* Audio element */}
      <audio ref={audioRef} loop preload="auto">
        <source src="/audio/nature-lofi-hummingbird.mp3" type="audio/mpeg" />
      </audio>

      {/* Initial prompt for first-time visitors */}
      {showInitialPrompt && (
        <div className="fixed bottom-24 left-4 z-50 animate-in slide-in-from-left-4 duration-300">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-[#006847]/20 p-4 max-w-xs">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-[#006847]/10 rounded-full flex items-center justify-center">
                <span className="text-lg">🐦</span>
              </div>
              <p className="text-sm font-medium text-gray-800">Ambient Sounds</p>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Experience our platform with gentle hummingbird nature sounds?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePromptResponse(true)}
                className="flex-1 px-3 py-1.5 bg-[#006847] text-white text-xs rounded-lg hover:bg-[#005a3c] transition-colors"
              >
                Enable
              </button>
              <button
                onClick={() => handlePromptResponse(false)}
                className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition-colors"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discreet floating player */}
      <div 
        className={`fixed bottom-6 left-4 z-40 transition-all duration-300 ${showInitialPrompt ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {isExpanded ? (
          // Expanded view with volume control
          <div className="bg-white/80 backdrop-blur-md rounded-full shadow-lg border border-gray-200/50 px-3 py-2 flex items-center gap-2 animate-in fade-in duration-200">
            <button
              onClick={toggleMute}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
              title={isMuted ? 'Unmute ambient sounds' : 'Mute ambient sounds'}
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4 text-gray-400" />
              ) : (
                <Volume2 className="h-4 w-4 text-[#006847]" />
              )}
            </button>
            
            {!isMuted && (
              <div className="w-20">
                <Slider
                  value={[volume]}
                  onValueChange={handleVolumeChange}
                  max={1}
                  step={0.05}
                  className="cursor-pointer"
                />
              </div>
            )}
            
            <span className="text-[10px] text-gray-400 ml-1">🐦</span>
          </div>
        ) : (
          // Minimized view - just a small indicator
          <button
            onClick={toggleMute}
            className={`w-10 h-10 rounded-full shadow-lg border flex items-center justify-center transition-all duration-300 ${
              isMuted 
                ? 'bg-white/60 border-gray-200/50 hover:bg-white/80' 
                : 'bg-white/80 border-[#006847]/20 hover:bg-white'
            }`}
            title={isMuted ? 'Enable ambient sounds' : 'Ambient sounds playing'}
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4 text-gray-400" />
            ) : (
              <div className="relative">
                <Volume2 className="h-4 w-4 text-[#006847]" />
                {isPlaying && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#A4D65E] rounded-full animate-pulse" />
                )}
              </div>
            )}
          </button>
        )}
      </div>
    </>
  );
}

export default MusicPlayer;